"use client";

import Image from "next/image";
import { useEffect, useId, useMemo, useState } from "react";

import { resolvePageImage, type SitePageImageMap } from "@/lib/site-page-images";

type Client = {
  id: string;
  name: string;
  serviceGroups: { label: string; items: string[] }[];
};

type ResolvedClient = Client & {
  imageSrc: string;
  imageAlt: string;
};

const CLIENTS: Client[] = [
  {
    id: "matthews",
    name: "Matthews Sports Vision",
    serviceGroups: [
      { label: "Brand development", items: [] },
      {
        label: "Website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
      {
        label: "Full funnel marketing",
        items: ["Social channels", "Email & SMS marketing", "SEO", "Google Ads"],
      },
    ],
  },
  {
    id: "sealord",
    name: "Sealord Skipper",
    serviceGroups: [
      {
        label: "E-commerce website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
      { label: "Marketing", items: ["SEO setup", "SEO campaign"] },
    ],
  },
  {
    id: "cameron",
    name: "Cameron Lawyers",
    serviceGroups: [
      { label: "Brand development", items: [] },
      {
        label: "Website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
      {
        label: "Full funnel marketing",
        items: ["Social channels", "Email & SMS marketing", "SEO", "Google Ads"],
      },
    ],
  },
  {
    id: "laffare",
    name: "L'affare",
    serviceGroups: [
      {
        label: "E-commerce website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
      {
        label: "Full funnel marketing",
        items: ["Social channels", "Email & SMS marketing", "SEO", "Google Ads"],
      },
    ],
  },
  {
    id: "hawker",
    name: "Hawker House",
    serviceGroups: [
      {
        label: "Website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
    ],
  },
  {
    id: "custompak",
    name: "Custom-Pak",
    serviceGroups: [
      {
        label: "Website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
      {
        label: "Full funnel marketing",
        items: ["Social channels", "Email & SMS marketing", "SEO", "Google Ads"],
      },
    ],
  },
  {
    id: "harbour",
    name: "Harbour Fish",
    serviceGroups: [
      {
        label: "E-commerce website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
      {
        label: "Full funnel marketing",
        items: ["Social channels", "Email & SMS marketing", "SEO", "Google Ads"],
      },
    ],
  },
  {
    id: "muros",
    name: "Muros",
    serviceGroups: [
      {
        label: "Website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
    ],
  },
  {
    id: "sealord-web",
    name: "Sealord",
    serviceGroups: [
      {
        label: "Website",
        items: ["Concept", "UX and UI design", "Full development"],
      },
    ],
  },
];

export function ClientsSection({
  pageImageMap,
  imageKeyPrefix,
}: {
  pageImageMap?: SitePageImageMap;
  /** Optional override keys: `{imageKeyPrefix}-client-{id}` (id from CLIENTS). */
  imageKeyPrefix?: string;
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isLg, setIsLg] = useState(false);
  const displayIndex = hoveredIndex ?? selectedIndex;

  const clients: ResolvedClient[] = useMemo(
    () =>
      CLIENTS.map((c) => {
        const keyWithPrefix = imageKeyPrefix ? `${imageKeyPrefix}-client-${c.id}` : undefined;
        const key = keyWithPrefix && pageImageMap?.[keyWithPrefix] ? keyWithPrefix : `client-${c.id}`;
        const { src, alt } = resolvePageImage(key, pageImageMap);
        return { ...c, imageSrc: src, imageAlt: alt };
      }),
    [pageImageMap, imageKeyPrefix],
  );

  const active = clients[displayIndex]!;
  const panelId = useId();
  const tablistId = useId();

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const sync = () => {
      setIsLg(mql.matches);
      if (!mql.matches) setHoveredIndex(null);
    };
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, []);

  return (
    <section
      className="relative bg-[#1b2631] py-16 font-sans text-white md:py-20"
      aria-labelledby="clients-heading"
    >
      <div className="site-content pt-4 md:pt-8">
        <div className="grid grid-cols-1 gap-y-10 gap-x-0 lg:grid-cols-2 lg:items-start lg:gap-x-16 lg:gap-y-16 xl:gap-x-20 xl:gap-y-20">
          <div className="min-w-0">
            <p className="text-sm font-medium tracking-wide text-[#8fa8bf] md:text-base">
              Clients
            </p>
            <h2
              id="clients-heading"
              className="mt-4 text-4xl leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              <span>
                The <span className="text-[#ff5c00]">awesome </span>
              </span>
              <span>
                <span className="text-[#ff5c00]">people</span> we
              </span>
              <span> work with</span>
            </h2>
          </div>

          <div className="flex min-w-0 flex-col lg:pt-0">
            <p className="text-4xl font-medium leading-none text-[#ff5c00] md:text-5xl" aria-hidden>
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-white/90" />
            <p className="mt-6 text-2xl font-medium leading-snug text-[#ff5c00] sm:text-3xl md:text-4xl lg:text-[2.75rem] lg:leading-12">
              Our clients – Brands ready to stand apart, not interested in blending in
            </p>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-white/95 md:mt-6 md:text-lg">
              No matter your size or stage, we partner with brands ready to challenge
              convention, creating bold campaigns and digital experiences that cut through
              noise, attract attention, and turn interest into growth.
            </p>
          </div>

          <div className="flex min-w-0 flex-col items-start lg:mt-0">
            <ul
              id={tablistId}
              className="flex flex-col w-full"
              role={isLg ? "tablist" : undefined}
              aria-label="Select a client"
              onPointerLeave={() => {
                if (isLg) setHoveredIndex(null);
              }}
            >
              {clients.map((client, index) => {
                const isDisplayed = index === displayIndex;
                const isSelected = index === selectedIndex;
                const mobilePanelId = `${panelId}-m-${client.id}`;
                return (
                  <li
                    key={client.id}
                    role={isLg ? "presentation" : undefined}
                    className="py-2.5"
                    onPointerEnter={() => {
                      if (isLg) setHoveredIndex(index);
                    }}
                  >
                    <button
                      type="button"
                      role={isLg ? "tab" : undefined}
                      id={`${tablistId}-${client.id}`}
                      aria-expanded={isLg ? undefined : isSelected}
                      aria-selected={isLg ? isSelected : undefined}
                      aria-controls={isLg ? panelId : mobilePanelId}
                      tabIndex={0}
                      onClick={() => setSelectedIndex(index)}
                      onFocus={() => {
                        if (isLg) setHoveredIndex(index);
                      }}
                      onBlur={(e) => {
                        if (!isLg) return;
                        const list = e.currentTarget.closest("ul");
                        const next = e.relatedTarget;
                        if (
                          list &&
                          (!next || !(next instanceof Node) || !list.contains(next))
                        ) {
                          setHoveredIndex(null);
                        }
                      }}
                      className={`w-full text-left text-4xl font-medium tracking-tight transition-colors duration-200 sm:text-3xl lg:text-4xl ${
                        isDisplayed
                          ? "text-[#ff5c00]"
                          : "text-[#8fa8bf]/80 lg:hover:text-[#8fa8bf]"
                      }`}
                    >
                      {client.name}
                    </button>

                    <div
                      id={mobilePanelId}
                      className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out lg:hidden ${
                        isSelected
                          ? "max-h-[1400px] opacity-100"
                          : "max-h-0 opacity-0"
                      }`}
                      aria-hidden={!isSelected}
                    >
                      <div className="clients-panel-enter flex flex-col gap-8 pt-4">
                        <div className="flex w-full flex-row items-start gap-x-4 gap-y-6 sm:gap-x-12 sm:gap-y-8">
                          {client.serviceGroups.map((group) => (
                            <div key={group.label} className="min-w-0 flex-1 basis-0">
                              <p className="text-base font-semibold uppercase tracking-wide text-[#8fa8bf]/80 sm:text-[#ff5c00] sm:text-lg">
                                {group.label}
                              </p>
                              <ul className="mt-2 space-y-1 text-xs leading-tight text-white/95 sm:text-sm md:text-lg">
                                {group.items.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>

                        <div className="w-full shrink-0">
                          <div className="rounded-2xl sm:rounded-[2.5rem] md:rounded-[2.75rem]">
                            <div className="relative aspect-9/17 w-full overflow-hidden rounded-xl sm:rounded-[1.85rem] md:rounded-[2.125rem]">
                              <Image
                                src={client.imageSrc}
                                alt={client.imageAlt}
                                fill
                                sizes="(max-width: 1023px) min(100vw - 2rem, 42rem), 384px"
                                className="object-cover"
                                priority={index === 0 && selectedIndex === 0}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div
            id={panelId}
            role="tabpanel"
            aria-labelledby={`${tablistId}-${active.id}`}
            className="min-w-0 hidden lg:block xl:-ml-62"
          >
            <div
              key={active.id}
              className="clients-panel-enter flex min-h-0 flex-col gap-10 lg:flex-row lg:items-end lg:gap-12"
            >
              <div className="flex w-[200px] flex-col gap-x-12 gap-y-8">
                {active.serviceGroups.map((group) => (
                  <div key={group.label}>
                    <p className="text-lg font-semibold uppercase tracking-wide text-[#ff5c00]">
                      {group.label}
                    </p>
                    <ul className="mt-2 space-y-1 text-sm leading-tight text-white/95 md:text-lg">
                      {group.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mx-auto w-full max-w-[16rem] shrink-0 sm:max-w-[18rem] lg:mx-0 lg:max-w-[min(26rem,32vw)]">
                <div className="rounded-[2.5rem] md:rounded-[2.75rem] lg:rounded-[3rem]">
                  <div className="relative aspect-9/17 w-full overflow-hidden rounded-[1.85rem] md:rounded-[2.125rem] lg:rounded-4xl">
                    <Image
                      src={active.imageSrc}
                      alt={active.imageAlt}
                      fill
                      sizes="(max-width: 1023px) 72vw, 320px"
                      className="object-cover"
                      priority={displayIndex === 0}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
